/**
 * WUA Fast Match beta — student-facing interview.
 *
 * Route:  /beta/course-fit
 * Depends on: /api/beta/course-fit/{flag,release,session,...}
 *
 * Flow states (`view` in local state):
 *   loading       — probing feature flag
 *   unavailable   — flag disabled OR release fetch failed
 *   entry         — approved copy + "Start Fast Match"
 *   interview     — the 14-screen flow (index 0..13)
 *   scoring       — brief transition after Q14 while backend scores
 *   results-ready — temporary results shell (prompt 04 will design cards)
 *   session-expired — TTL / missing session on resume
 *
 * State discipline (matches 04_UX_AND_COPY_SPEC.md):
 *   • Q02 clears if its topic is dropped from Q01.
 *   • Q03 selections are re-filtered whenever Q01 or Q02 changes.
 *   • Any Q01–Q13 edit clears the Q14 offer + results (server also
 *     enforces this — see engine tests test_editing_q1_q13_invalidates
 *     _q14_offer).
 *   • Q14 subject binding stays entirely on the server; the client
 *     only knows "left" / "right" / "both" / "neither".
 *
 * Keyboard: focus moves to the screen H1 after every navigation.
 * Reduced motion: `prefers-reduced-motion` disables the scoring spinner.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, FlaskConical, Loader2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  fetchFlag, fetchRelease, createSession, saveAnswer,
  buildPreliminary, completeSession, fetchResumeState, fetchResult,
  fetchMyResults, fetchMyResult, fetchMyAnswers, attachSession,
  loadStoredSession, clearStoredSession,
  trackCourseFitEvent, FAST_MATCH_CLIENT_EVENTS,
} from "@/pages/beta/courseFitApi";
import {
  Q01GroupedMulti, FilteredMulti, FixedMulti, SingleChoice,
  OptionalMultiWithNone, VignettePair,
} from "@/pages/beta/courseFitScreens";
import CourseFitResults from "@/pages/beta/CourseFitResults";
import { InterestsStep } from "@/pages/beta/FastMatchInterests";
import { useAuth } from "@/context/AuthContext";


/* ================================================================== */
/* Shell primitives                                                    */
/* ================================================================== */

const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-[#FBF8F3]" data-testid={testId}>
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
      {children}
    </div>
  </div>
);

const Header = ({ questionIndex, totalQuestions }) => (
  <header className="mb-6" data-testid="course-fit-header">
    <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-slate-500">
      <span className="flex items-center gap-1.5">
        <FlaskConical className="h-3 w-3" aria-hidden="true" />
        WUA Fast Match
        <span
          className="ml-1 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-wider text-amber-900"
          data-testid="course-fit-beta-badge"
        >
          Beta
        </span>
      </span>
      {typeof questionIndex === "number" && (
        <span data-testid="course-fit-progress-label">
          Question {questionIndex + 1} of {totalQuestions}
        </span>
      )}
    </div>
    {typeof questionIndex === "number" && (
      <div
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={totalQuestions}
        aria-valuenow={questionIndex + 1}
        aria-label={`Progress: question ${questionIndex + 1} of ${totalQuestions}`}
        className="mt-3 h-1 w-full overflow-hidden rounded-full bg-slate-200"
        data-testid="course-fit-progress-bar"
      >
        <div
          className="h-full rounded-full bg-slate-900 transition-[width] motion-reduce:transition-none"
          style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
        />
      </div>
    )}
  </header>
);


/* ================================================================== */
/* Entry screen                                                        */
/* ================================================================== */

function EntryScreen({ releaseIdentifiers, onStart, starting, inviteInfo }) {
  return (
    <Shell testId="course-fit-entry">
      <Header />
      {inviteInfo && (
        <div
          role="status"
          data-testid="fm-invite-banner"
          className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900"
        >
          <div className="font-semibold">
            You were invited by {inviteInfo.school || "your school"}
          </div>
          <p className="mt-1 text-purple-900/90">
            Your counsellor will be able to see whether you complete Fast
            Match. Your answers and results remain private unless you
            separately choose to share them.
          </p>
        </div>
      )}
      <h1
        tabIndex={-1}
        className="text-4xl font-editorial tracking-tight text-slate-900 sm:text-5xl mt-4"
        data-testid="course-fit-entry-heading"
      >
        Find subjects that fit how you think
      </h1>
      <p className="mt-4 max-w-2xl text-base text-slate-700 sm:text-lg">
        In about three minutes, choose the ideas and study experiences that appeal
        to you. WUA Fast Match will compare your answers with 57 calibrated subject
        profiles across every WUA subject family.
      </p>
      <button
        type="button"
        onClick={onStart}
        disabled={starting}
        data-testid="course-fit-entry-start"
        className={cn(
          "mt-8 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-3 text-base font-medium text-white transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
          starting ? "opacity-70" : "hover:bg-slate-800",
        )}
      >
        {starting && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
        Start Fast Match
      </button>
      <p className="mt-6 max-w-xl text-[13px] text-slate-500">
        This beta explores Course Fit. It does not test your ability or predict admission.
      </p>
      {releaseIdentifiers?.fastMatchVersion && (
        <p className="mt-10 text-[11px] text-slate-400" data-testid="course-fit-entry-model">
          Model: {releaseIdentifiers.fastMatchVersion}
        </p>
      )}
    </Shell>
  );
}


/* ================================================================== */
/* Unavailable / expired / results-ready panels                        */
/* ================================================================== */

function UnavailablePanel() {
  return (
    <Shell testId="course-fit-unavailable">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-4">
        <Link to="/" className="hover:text-slate-800 inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to WUA
        </Link>
      </div>
      <h1 tabIndex={-1} className="text-4xl sm:text-5xl font-editorial tracking-tight text-slate-900">
        This page isn't available.
      </h1>
      <p className="mt-4 max-w-xl text-base text-slate-600">
        Fast Match is currently paused for release preparation. Please check back soon.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50"
        data-testid="course-fit-unavailable-home"
      >
        Return home
      </Link>
    </Shell>
  );
}

function ExpiredPanel({ onRestart }) {
  return (
    <Shell testId="course-fit-expired">
      <Header />
      <h1 tabIndex={-1} className="text-3xl sm:text-4xl font-editorial tracking-tight text-slate-900">
        This Fast Match session has expired.
      </h1>
      <p className="mt-4 max-w-xl text-base text-slate-600">
        Start again to get a fresh result.
      </p>
      <button
        type="button"
        onClick={onRestart}
        data-testid="course-fit-expired-restart"
        className="mt-6 inline-flex items-center gap-1.5 rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" />
        Start over
      </button>
    </Shell>
  );
}

function ScoringPanel() {
  return (
    <Shell testId="course-fit-scoring">
      <Header />
      <div className="mt-16 flex items-center gap-3 text-slate-700">
        <Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        <span aria-live="polite" data-testid="course-fit-scoring-status">
          Calculating your results…
        </span>
      </div>
      <p className="mt-3 text-sm text-slate-500">
        Your answers are being compared with 57 calibrated subject profiles.
      </p>
    </Shell>
  );
}

function ResultsReadyPanel(props) {
  return <CourseFitResults {...props} />;
}


/* ================================================================== */
/* Nav footer                                                           */
/* ================================================================== */

function NavFooter({
  onBack, backLabel, canBack,
  onContinue, continueLabel, canContinue,
  continueTestId, saving, errorMessage, onRetry,
}) {
  return (
    <>
      {errorMessage && (
        <div
          role="alert"
          data-testid="course-fit-save-error"
          className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {errorMessage}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              data-testid="course-fit-save-retry"
              className="ml-3 rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-900 hover:bg-red-100"
            >
              Retry
            </button>
          )}
        </div>
      )}
      <nav className="mt-8 flex items-center justify-between" data-testid="course-fit-nav">
        {canBack ? (
          <button
            type="button"
            onClick={onBack}
            data-testid="course-fit-back"
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden="true" />
            {backLabel || "Back"}
          </button>
        ) : <span />}
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue || saving}
          data-testid={continueTestId || "course-fit-continue"}
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
            canContinue && !saving
              ? "bg-slate-900 text-white hover:bg-slate-800"
              : "cursor-not-allowed bg-slate-200 text-slate-500",
          )}
        >
          {saving && <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {continueLabel || "Continue"}
          {!saving && <ArrowRight className="h-3 w-3" aria-hidden="true" />}
        </button>
      </nav>
    </>
  );
}


/* ================================================================== */
/* Selection-count helper text (announced to a11y)                     */
/* ================================================================== */

function SelectionCounter({ selectedCount, min, max, questionId }) {
  let msg;
  if (min === max) msg = `Choose ${min}. You have chosen ${selectedCount}.`;
  else if (max === 2 && min === 0) msg = `Optional — you can choose up to 2. You have chosen ${selectedCount}.`;
  else if (max === 2 && min === 1) msg = `Choose 1 or 2. You have chosen ${selectedCount}.`;
  else msg = `Choose ${min} to ${max}. You have chosen ${selectedCount}.`;
  return (
    <p
      aria-live="polite"
      className="mt-2 text-xs text-slate-500"
      data-testid={`${questionId.toLowerCase()}-selection-counter`}
    >
      {msg}
    </p>
  );
}


/* ================================================================== */
/* Screen router                                                       */
/* ================================================================== */

function renderScreen({ question, topicGroups, answers, setAnswer, q14 }) {
  const value = answers[question.questionId] || [];
  const setValue = (next) => setAnswer(question.questionId, next);

  if (question.questionId === "Q01") {
    return (
      <Q01GroupedMulti
        question={question}
        topicGroups={topicGroups}
        value={value}
        onChange={setValue}
      />
    );
  }
  if (question.questionId === "Q02") {
    const q01 = answers.Q01 || [];
    // Q02 offers the same option pool as Q01 but only rows whose optionCode
    // appears in Q01's selected codes.
    const q01Codes = new Set(q01.map((oid) => oid.replace(/^Q01_/, "")));
    // Build inclusion set of Q02 option ids by matching optionCode
    const includeIds = new Set(
      (question.options || [])
        .filter((o) => q01Codes.has(o.optionCode))
        .map((o) => o.optionId),
    );
    return (
      <FilteredMulti
        question={question}
        includeOptionIds={includeIds}
        value={value}
        onChange={setValue}
      />
    );
  }
  if (question.questionId === "Q03") {
    const q01Codes = new Set((answers.Q01 || []).map((oid) => oid.replace(/^Q01_/, "")));
    const q02Codes = new Set((answers.Q02 || []).map((oid) => oid.replace(/^Q02_/, "")));
    const excludeCodes = new Set([...q01Codes, ...q02Codes]);
    return (
      <FilteredMulti
        question={question}
        excludeOptionCodes={excludeCodes}
        value={value}
        onChange={setValue}
        allowNoneOptionCode="__NONE__"
        noneLabel="Nothing here"
      />
    );
  }
  if (["Q04", "Q05", "Q06", "Q12"].includes(question.questionId)) {
    return <FixedMulti question={question} value={value} onChange={setValue} />;
  }
  if (["Q07", "Q08", "Q09", "Q13"].includes(question.questionId)) {
    return <SingleChoice question={question} value={value} onChange={setValue} />;
  }
  if (["Q10", "Q11"].includes(question.questionId)) {
    return (
      <OptionalMultiWithNone
        question={question}
        value={value}
        onChange={setValue}
        noneLabel="None of these would concern me"
      />
    );
  }
  if (question.questionId === "Q14") {
    return (
      <VignettePair
        offer={q14.offer}
        choice={q14.choice}
        onChoose={q14.setChoice}
      />
    );
  }
  return null;
}

const _rangeText = (q) => {
  if (q.minSelections === q.maxSelections) return `Choose ${q.minSelections}.`;
  if (q.minSelections === 0) return `Optional — up to ${q.maxSelections}.`;
  return `Choose ${q.minSelections} to ${q.maxSelections}.`;
};


/* ================================================================== */
/* Main component                                                      */
/* ================================================================== */

export default function BetaCourseFit() {
  const [view, setView] = useState("loading");
  const [flag, setFlag] = useState(null);
  const [release, setRelease] = useState(null);
  const [session, setSession] = useState(null);          // {sessionId, accessToken}
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [snapshotId, setSnapshotId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [answers, setAnswers] = useState({});
  const [screenIndex, setScreenIndex] = useState(0);
  const [q14Offer, setQ14Offer] = useState(null);
  const [q14Choice, setQ14Choice] = useState(null);
  // Set only if the browser landed with a valid #invite=… fragment
  // OR already carries a wua_fm_invite cookie from a prior redeem.
  // Never contains the token; never contains counsellor or student
  // identity. `{ school, expires_at }` at most.
  const [inviteInfo, setInviteInfo] = useState(null);
  const headingRef = useRef(null);
  const pendingRetryRef = useRef(null);

  /* ---------- #invite= redemption (fragment scrubbed after) ------ */
  useEffect(() => {
    let live = true;
    let redeemed = false;
    const scrubFragment = () => {
      try {
        if (window.history?.replaceState) {
          const { origin, pathname, search } = window.location;
          window.history.replaceState({}, "", `${origin}${pathname}${search}`);
        }
      } catch (_e) { /* ignore */ }
    };
    const tryRedeem = async () => {
      if (!live || redeemed) return;
      const raw = window.location.hash || "";
      const m = raw.match(/^#invite=([A-Za-z0-9_\-]{20,200})$/);
      if (!m) return;
      redeemed = true;
      const token = m[1];
      scrubFragment();
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/course-fit/invite/redeem`;
        await fetch(url, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
      } catch (_e) { /* silent */ }
      // Re-probe banner state.
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/course-fit/invite/self`;
        const r = await fetch(url, { credentials: "include" });
        if (r.ok && live) {
          const j = await r.json();
          if (j?.invited) setInviteInfo({ school: j.school, expires_at: j.expires_at });
        }
      } catch (_e) { /* ignore */ }
    };
    // 1) Preview environments sometimes strip and re-apply the fragment
    //    after first paint. Retry a few times in the first ~2s so we
    //    survive that race. 2) Also listen for the `hashchange` event.
    let attempts = 0;
    const poll = () => {
      tryRedeem();
      attempts += 1;
      if (attempts < 10 && !redeemed && live) {
        setTimeout(poll, 200);
      }
    };
    poll();
    const onHashChange = () => tryRedeem();
    window.addEventListener("hashchange", onHashChange);
    // NOTE: the baseline `/invite/self` probe is intentionally NOT
    // fired on every entry. It runs only:
    //   (a) after a successful `tryRedeem()` (inside that fn), and
    //   (b) if the URL landed with a `#invite=` fragment that failed
    //       to redeem but the browser might already carry a cookie
    //       from an earlier tab — handled by the `hashchange` listener.
    // Rationale: keep the entry-page network profile unchanged for
    // the vast majority of Fast Match users who arrive without an
    // invite, so Playwright `networkidle` waits (used by the Stage
    // 03 UI regression suite) settle at their historical timing.
    return () => {
      live = false;
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  /* ---------- saved results for signed-in owners ----------------- */
  const { user, loading: authLoading } = useAuth();
  const [history, setHistory] = useState(null);
  const [savedSessionId, setSavedSessionId] = useState(null);
  const [interestsPrefillFrom, setInterestsPrefillFrom] = useState(null);
  const [planFrom, setPlanFrom] = useState(null);
  const openSaved = useCallback(async () => {
    if (!user) return false;
    const r = await fetchMyResults();
    if (!r.ok || !r.data?.latest) return false;
    setHistory(r.data.attempts);
    setSnapshot(r.data.latest);
    setSnapshotId(r.data.latest.snapshotId);
    setSavedSessionId(r.data.latest.sessionId);
    setView("results-ready");
    return true;
  }, [user]);
  const refreshHistory = useCallback(async () => {
    if (!user) return;
    const r = await fetchMyResults();
    if (r.ok) setHistory(r.data.attempts);
  }, [user]);

  /* ---------- flag + release probe + optional resume ------------ */
  useEffect(() => {
    if (authLoading) return undefined;
    let live = true;
    (async () => {
      try {
        const [f, r] = await Promise.all([fetchFlag(), fetchRelease()]);
        if (!live) return;
        setFlag(f);
        setRelease(r);
        if (f?.enabled !== true) {
          setView("unavailable");
          return;
        }
        // If a browser tab holds a session token, try to resume it
        // before showing the entry screen. On any failure (expired,
        // token mismatch, network error) fall through to the entry
        // screen — the student can start fresh.
        const stored = loadStoredSession();
        if (!stored?.sessionId) {
          if (!(await openSaved())) setView("entry");
          return;
        }
        const resumed = await fetchResumeState(stored.sessionId, stored.accessToken);
        if (!live) return;
        if (!resumed.ok) {
          // Any failure invalidates the local session token. A signed-in
          // owner falls back to their latest saved result.
          clearStoredSession();
          if (!(await openSaved())) setView("entry");
          return;
        }
        // Hydrate the reducer from the server's persisted state.
        setSession(stored);
        const d = resumed.data;
        // Answers: Q10/Q11 arrive as [] when the user picked "None".
        // Convert to the local __NONE__ affordance so Continue enables
        // without a re-click.
        const localAnswers = {};
        const rawAns = d.answers || {};
        Object.entries(rawAns).forEach(([qid, ids]) => {
          if ((qid === "Q10" || qid === "Q11") && Array.isArray(ids) && ids.length === 0) {
            localAnswers[qid] = ["__NONE__"];
          } else {
            localAnswers[qid] = Array.isArray(ids) ? ids : [];
          }
        });
        setAnswers(localAnswers);
        if (d.resultReady) {
          setSnapshotId(d.snapshotId);
          // Deep-return: fetch the full immutable snapshot with the
          // student's access token.
          const r = await fetchResult(stored.sessionId, stored.accessToken);
          if (!live) return;
          if (r.ok) {
            setSnapshot(r.data);
            setView("results-ready");
          } else {
            // Access denied / expired — fall back to entry.
            clearStoredSession();
            setView("entry");
          }
          return;
        }
        if (d.tieBreakOffer) {
          setQ14Offer(d.tieBreakOffer);
          setScreenIndex((r?.totalQuestions || 14) - 1);  // Q14
          setView("interview");
          return;
        }
        // Land on the last-answered question so the user sees their
        // last selection restored (spec: "most recently valid screen").
        const answered = (r?.questions || [])
          .map((q, i) => [q.questionId, i])
          .filter(([qid]) => qid in rawAns);
        const idx = answered.length ? answered[answered.length - 1][1] : 0;
        setScreenIndex(idx);
        setView("interview");
      } catch {
        if (!live) return;
        setView("unavailable");
      }
    })();
    return () => { live = false; };
  }, [authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- keyboard focus + progressive announcements --------- */
  useEffect(() => {
    if (view === "interview" || view === "interests" || view === "entry" || view === "results-ready" || view === "expired") {
      // Move focus to the H1 after each screen transition so screen-
      // reader users hear the new question.
      requestAnimationFrame(() => headingRef.current?.focus?.());
    }
  }, [view, screenIndex]);

  /* ---------- client analytics: screen_viewed per interview screen */
  const screenEnteredAtRef = useRef(null);
  useEffect(() => {
    if (view !== "interview" || !session?.sessionId || !session?.accessToken) return;
    const q = release?.questions?.[screenIndex];
    if (!q) return;
    screenEnteredAtRef.current = Date.now();
    trackCourseFitEvent(
      session.sessionId, session.accessToken,
      FAST_MATCH_CLIENT_EVENTS.SCREEN_VIEWED,
      { questionId: q.questionId, sequence: screenIndex + 1 },
    );
  }, [view, screenIndex, session, release]);

  const questions = release?.questions || [];
  const totalQuestions = release?.totalQuestions || 14;
  const currentQuestion = questions[screenIndex] || null;

  /* ---------- dependent-answer rebuild --------------------------- */
  const rebuildDependents = useCallback((qid, prev) => {
    const next = { ...prev };
    if (qid === "Q01") {
      // Q02 must remain inside Q01. Clear if not.
      const q01Codes = new Set((next.Q01 || []).map((oid) => oid.replace(/^Q01_/, "")));
      if (next.Q02 && next.Q02.length) {
        const stillValid = next.Q02.every((oid) => q01Codes.has(oid.replace(/^Q02_/, "")));
        if (!stillValid) delete next.Q02;
      }
      // Q03 cannot include Q01 or Q02 topics.
      if (next.Q03 && next.Q03.length) {
        const q02Codes = new Set((next.Q02 || []).map((oid) => oid.replace(/^Q02_/, "")));
        const forbid = new Set([...q01Codes, ...q02Codes]);
        next.Q03 = next.Q03.filter((oid) => !forbid.has(oid.replace(/^Q03_/, "")));
      }
    }
    if (qid === "Q02" && next.Q03) {
      const q01Codes = new Set((next.Q01 || []).map((oid) => oid.replace(/^Q01_/, "")));
      const q02Codes = new Set((next.Q02 || []).map((oid) => oid.replace(/^Q02_/, "")));
      const forbid = new Set([...q01Codes, ...q02Codes]);
      next.Q03 = next.Q03.filter((oid) => !forbid.has(oid.replace(/^Q03_/, "")));
    }
    // Any Q01–Q13 change: server will invalidate Q14 offer + result;
    // mirror on the client so a stale offer never lingers.
    if (qid !== "Q14") {
      if (q14Offer) setQ14Offer(null);
      if (q14Choice) setQ14Choice(null);
    }
    return next;
  }, [q14Offer, q14Choice]);

  const setAnswer = useCallback((qid, next) => {
    setAnswers((prev) => rebuildDependents(qid, { ...prev, [qid]: next }));
  }, [rebuildDependents]);

  /* ---------- selection validity --------------------------------- */
  const isCurrentValid = useMemo(() => {
    if (!currentQuestion) return false;
    if (currentQuestion.questionId === "Q14") return q14Choice !== null;
    const value = answers[currentQuestion.questionId] || [];
    // Q10/Q11 "None" is stored as ["__NONE__"] client-side; treat as valid 0.
    const isNone = value.length === 1 && value[0] === "__NONE__";
    const effLen = isNone ? 0 : value.length;
    return effLen >= currentQuestion.minSelections && effLen <= currentQuestion.maxSelections;
  }, [currentQuestion, answers, q14Choice]);

  /* ---------- start / save / navigate ---------------------------- */
  const handleStart = useCallback(async () => {
    setStarting(true);
    try {
      const s = session?.sessionId ? session : await createSession();
      setSession({ sessionId: s.sessionId, accessToken: s.accessToken });
      setScreenIndex(0);
      setInterestsPrefillFrom(null);
      setView("interests");
    } catch {
      setSaveError("We could not start Fast Match right now. Please try again.");
    } finally {
      setStarting(false);
    }
  }, [session]);

  const handleRestart = useCallback(() => {
    clearStoredSession();
    setSavedSessionId(null);
    setSession(null);
    setAnswers({});
    setQ14Offer(null);
    setQ14Choice(null);
    setSnapshotId(null);
    setSnapshot(null);
    setScreenIndex(0);
    setSaveError(null);
    setPlanFrom(null);
    setView("entry");
  }, []);

  // "Edit my answers": a NEW attempt prefilled with a completed attempt's
  // answers. The earlier attempt and its result stay untouched.
  const handleEditAnswers = useCallback(async (fromSessionId) => {
    setSaveError(null);
    const prev = await fetchMyAnswers(fromSessionId);
    if (!prev.ok) { setSaveError("We couldn't load your earlier answers. Try again."); return; }
    try {
      clearStoredSession();
      const s = await createSession();
      const order = (release?.questions || []).map((q) => q.questionId).filter((q) => q !== "Q14");
      const local = {};
      for (const qid of order) {
        const ids = prev.data.answers[qid];
        if (!Array.isArray(ids)) continue;
        const r = await saveAnswer(s.sessionId, qid, ids);
        if (!r.ok) throw new Error(qid);
        local[qid] = (qid === "Q10" || qid === "Q11") && ids.length === 0 ? ["__NONE__"] : ids;
      }
      setSession({ sessionId: s.sessionId, accessToken: s.accessToken });
      setAnswers(local);
      setQ14Offer(null);
      setQ14Choice(null);
      setSnapshot(null);
      setSnapshotId(null);
      setSavedSessionId(null);
      setScreenIndex(0);
      setInterestsPrefillFrom(fromSessionId);
      setPlanFrom(fromSessionId);
      setView("interests");
    } catch {
      setSaveError("We couldn't start a new attempt with your earlier answers. Your saved result is unchanged.");
    }
  }, [release]);

  const handleOpenAttempt = useCallback(async (sessionId) => {
    const r = await fetchMyResult(sessionId);
    if (!r.ok) { setSaveError("We couldn't open that earlier result."); return; }
    setSnapshot(r.data);
    setSnapshotId(r.data.snapshotId);
    setSavedSessionId(sessionId);
    setPlanFrom(null);
  }, []);

  const persistAnswerAndAdvance = useCallback(async () => {
    setSaveError(null);
    if (!session?.sessionId || !currentQuestion) return;
    setSaving(true);
    const qid = currentQuestion.questionId;
    let effectiveIds = answers[qid] || [];
    if (qid === "Q10" || qid === "Q11") {
      if (effectiveIds.length === 1 && effectiveIds[0] === "__NONE__") effectiveIds = [];
    }
    const result = await saveAnswer(session.sessionId, qid, effectiveIds);
    if (!result.ok) {
      const c = result.error?.code;
      // Analytics — screen error (question id + error code only; no raw
      // token, no answer payload).
      trackCourseFitEvent(
        session.sessionId, session.accessToken,
        FAST_MATCH_CLIENT_EVENTS.SCREEN_ERROR,
        { questionId: qid, errorCode: c || "network_error" },
      );
      if (c === "session_not_found") {
        setSaving(false);
        setView("expired");
        return;
      }
      if (c === "stale_sequence") {
        // A duplicate submit — treat as no-op success.
        setSaving(false);
        setScreenIndex((i) => Math.min(i + 1, totalQuestions - 1));
        return;
      }
      setSaving(false);
      setSaveError("We could not save that answer. Your choices are still on this device. Try again.");
      pendingRetryRef.current = persistAnswerAndAdvance;
      return;
    }
    // Analytics — successful answer save (selectionCount + elapsed bucket).
    const enteredAt = screenEnteredAtRef.current || Date.now();
    const elapsedSec = Math.max(0, (Date.now() - enteredAt) / 1000);
    trackCourseFitEvent(
      session.sessionId, session.accessToken,
      FAST_MATCH_CLIENT_EVENTS.ANSWER_SAVED,
      {
        questionId: qid,
        selectionCount: effectiveIds.length,
        elapsedBucket: (
          elapsedSec < 10 ? "<10s" :
          elapsedSec < 30 ? "10-30s" :
          elapsedSec < 60 ? "30-60s" :
          elapsedSec < 120 ? "60-120s" : ">120s"
        ),
      },
    );
    setSaving(false);
    if (screenIndex < totalQuestions - 2) {
      setScreenIndex(screenIndex + 1);
      return;
    }
    // After Q13 (index 12) advance triggers preliminary → then Q14 screen.
    if (screenIndex === totalQuestions - 2) {
      const pre = await buildPreliminary(session.sessionId);
      if (!pre.ok) {
        const c = pre.error?.code;
        if (c === "session_not_found") { setView("expired"); return; }
        setSaveError("We could not calculate your results just now. Your answers have been kept. Try again.");
        pendingRetryRef.current = persistAnswerAndAdvance;
        return;
      }
      setQ14Offer(pre.data);
      setScreenIndex(totalQuestions - 1);
    }
  }, [session, currentQuestion, answers, screenIndex, totalQuestions]);

  const handleComplete = useCallback(async () => {
    if (!session?.sessionId || !q14Choice) return;
    setSaveError(null);
    setView("scoring");
    const r = await completeSession(session.sessionId, q14Choice);
    if (!r.ok) {
      const c = r.error?.code;
      if (c === "session_not_found" || c === "expired_offer") {
        setView("expired");
        return;
      }
      setSaveError("We could not calculate your results just now. Your answers have been kept. Try again.");
      pendingRetryRef.current = handleComplete;
      setView("interview");
      return;
    }
    setSnapshotId(r.data.snapshotId);
    setSnapshot(r.data);
    setView("results-ready");
    // Signed-in: keep the completed result with the account so it reopens later.
    if (user && session.accessToken) {
      const a = await attachSession(session.sessionId, session.accessToken);
      if (a.ok) { setSavedSessionId(session.sessionId); clearStoredSession(); refreshHistory(); }
    }
  }, [session, q14Choice, user, refreshHistory]);

  const handleBack = useCallback(() => {
    setSaveError(null);
    if (screenIndex > 0) setScreenIndex(screenIndex - 1);
  }, [screenIndex]);

  const handleRetry = useCallback(() => {
    setSaveError(null);
    const fn = pendingRetryRef.current;
    pendingRetryRef.current = null;
    if (fn) fn();
  }, []);

  /* ---------- render dispatch ------------------------------------ */
  if (view === "loading") {
    return (
      <Shell testId="course-fit-loading">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          Loading …
        </div>
      </Shell>
    );
  }
  if (view === "unavailable") return <UnavailablePanel />;
  if (view === "expired") return <ExpiredPanel onRestart={handleRestart} />;
  if (view === "scoring") return <ScoringPanel />;
  if (view === "results-ready") return (
    <ResultsReadyPanel
      snapshot={snapshot}
      onStartOver={handleRestart}
      session={savedSessionId && session?.sessionId !== savedSessionId ? { sessionId: savedSessionId, accessToken: null } : session}
      saved={{ signedIn: !!user, sessionId: savedSessionId, history: history || [], onEdit: handleEditAnswers, onOpen: handleOpenAttempt, error: saveError }}
      planFrom={planFrom && snapshot?.sessionId !== planFrom ? planFrom : null}
    />
  );
  if (view === "entry") return (
    <EntryScreen
      releaseIdentifiers={release?.modelVersions}
      onStart={handleStart}
      starting={starting}
      inviteInfo={inviteInfo}
    />
  );

  if (view === "interests" && session?.sessionId) return (
    <Shell testId="course-fit-interests">
      <Header />
      <InterestsStep session={session} prefillFrom={interestsPrefillFrom} headingRef={headingRef}
        onDone={() => { setScreenIndex(0); setView("interview"); }} />
    </Shell>
  );

  /* view === "interview" */
  if (!currentQuestion) return <UnavailablePanel />;
  const value = answers[currentQuestion.questionId] || [];
  const isNone = value.length === 1 && value[0] === "__NONE__";
  const effLen = isNone ? 0 : value.length;
  const isQ14 = currentQuestion.questionId === "Q14";

  return (
    <Shell testId="course-fit-interview">
      <Header questionIndex={screenIndex} totalQuestions={totalQuestions} />
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-2xl font-editorial tracking-tight text-slate-900 sm:text-3xl focus:outline-none"
        data-testid={`${currentQuestion.questionId.toLowerCase()}-title`}
      >
        {isQ14 ? "One final choice" : currentQuestion.screenTitle}
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base" data-testid={`${currentQuestion.questionId.toLowerCase()}-prompt`}>
        {isQ14
          ? "Choose the study experience that feels more like you — or say both feel equal, or neither."
          : currentQuestion.questionId === "Q01"
          ? "Which subjects or ideas would you most enjoy exploring for several years?"
          : currentQuestion.prompt}
      </p>
      {!isQ14 && (
        <p className="mt-1 text-xs text-slate-500" data-testid={`${currentQuestion.questionId.toLowerCase()}-range`}>
          {currentQuestion.questionId === "Q01"
            ? "Choose 2 to 5 from any of the subject areas. Leaving one out does not mean you dislike it."
            : _rangeText(currentQuestion)}
        </p>
      )}
      <div className="mt-6" data-testid={`${currentQuestion.questionId.toLowerCase()}-body`}>
        {renderScreen({
          question: currentQuestion,
          topicGroups: release?.topicGroups || [],
          answers,
          setAnswer,
          q14: { offer: q14Offer, choice: q14Choice, setChoice: setQ14Choice },
        })}
      </div>
      {!isQ14 && (
        <SelectionCounter
          selectedCount={effLen}
          min={currentQuestion.minSelections}
          max={currentQuestion.maxSelections}
          questionId={currentQuestion.questionId}
        />
      )}
      <NavFooter
        onBack={handleBack}
        canBack={screenIndex > 0}
        onContinue={isQ14 ? handleComplete : persistAnswerAndAdvance}
        canContinue={isCurrentValid}
        continueLabel={isQ14 ? "See results" : (screenIndex === totalQuestions - 2 ? "Show preview" : "Continue")}
        continueTestId={isQ14 ? "course-fit-complete" : "course-fit-continue"}
        saving={saving}
        errorMessage={saveError}
        onRetry={pendingRetryRef.current ? handleRetry : null}
      />
    </Shell>
  );
}
